import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowDown, ArrowUp, BookOpen, Eye, ListOrdered, Pencil, Plus, Save, Trash2 } from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  AppBadge, AppButton, AppCard, AppCardContent, AppCardHeader, AppCardTitle, ConfirmModal,
  EmptyState, FormActionBar, IconButton, MathText, PageHeader,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import CommonToaster from "../Common/CommonToaster";
import { PassageView } from "../QuizPassage";
import PassageForm, { emptyPassage, passagePayload, passageProblem } from "./PassageForm";
import { parentOf, passageTrail } from "./quizCenterTrail";

/**
 * One passage and the set of questions asked about it.
 *
 * The questions are ordinary bank questions — written in the question editor,
 * which is opened from here with the passage already chosen and comes back here
 * when it saves. Their order here is the order a sitting asks them in.
 */

const TYPE_LABELS = {
  single_choice: "Single choice",
  multi_select: "Multi select",
  true_false: "True / False",
  text_answer: "Text answer",
  fill_blank: "Fill in blank",
  numeric_answer: "Numeric",
  image_mcq: "Image MCQ",
  code_conceptual: "Code",
};

const fromServer = (p) => ({
  ...emptyPassage(),
  title: p.title || "",
  kind: p.kind === "pdf" ? "pdf" : "text",
  instructions: p.instructions || "",
  text: p.text || "",
  imageUrl: p.imageUrl || "",
  file: p.file || null,
  category: p.category?._id || p.category || "",
  subcategory: p.subcategory?._id || p.subcategory || "",
});

export default function PassageEditorPage() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const isLibrary = useLocation().pathname.startsWith("/sa/");
  const base = isLibrary ? "/sa/marketplace/quiz-questions" : "/quiz-bank";

  const [form, setForm] = useState(emptyPassage);
  const [saved, setSaved] = useState(null); // the passage as last saved
  const [questions, setQuestions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const say = (message, type = "success") => setToast({ show: true, message, type });

  useEffect(() => {
    apiClient.get("/quiz-categories?flat=true")
      .then(({ data }) => setCategories(data.categories || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/quiz-passages/${id}`);
      setForm(fromServer(data.passage));
      setSaved(data.passage);
      setQuestions(data.questions || []);
    } catch (err) {
      say(err.response?.data?.message || "Could not load the passage.", "error");
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  const update = useCallback((patch) => setForm((f) => ({ ...f, ...patch })), []);

  // The file being shown is either the saved one (read through the passage) or
  // one just uploaded and not saved yet (read by its key).
  const fileKey = form.file?.key || "";
  const loadFileUrl = useMemo(() => {
    if (!fileKey) return null;
    const isSavedFile = saved?.file?.key === fileKey;
    return async () => {
      const { data } = isSavedFile
        ? await apiClient.get(`/quiz-passages/${id}/file`)
        : await apiClient.get("/quiz-passages/upload-link", { params: { key: fileKey } });
      return data?.url || null;
    };
  }, [fileKey, saved, id]);

  const save = async (e) => {
    e?.preventDefault?.();
    const problem = passageProblem(form);
    if (problem) { say(problem, "error"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await apiClient.post("/quiz-passages", passagePayload(form));
        say("Passage saved — now add the questions about it.");
        navigate(`${base}/passages/${data.passage._id}`, { replace: true });
      } else {
        const { data } = await apiClient.put(`/quiz-passages/${id}`, passagePayload(form));
        setSaved(data.passage);
        say("Passage saved.");
      }
    } catch (err) {
      say(err.response?.data?.message || "Could not save the passage.", "error");
    } finally {
      setSaving(false);
    }
  };

  const move = async (index, delta) => {
    const to = index + delta;
    if (to < 0 || to >= questions.length) return;
    const next = [...questions];
    [next[index], next[to]] = [next[to], next[index]];
    const before = questions;
    setQuestions(next);
    try {
      await apiClient.put(`/quiz-passages/${id}/order`, { questionIds: next.map((q) => q._id) });
    } catch {
      setQuestions(before);
      say("Could not save the order.", "error");
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      const { data } = await apiClient.delete(`/quiz-passages/${id}`);
      say(data.message || "Passage deleted.");
      navigate(`${base}/passages`, { replace: true });
    } catch (err) {
      say(err.response?.data?.message || "Could not delete the passage.", "error");
      setDeleting(false);
    }
  };

  const addQuestionUrl = () => {
    const params = new URLSearchParams({ passage: id, from: "passage" });
    if (form.category) params.set("category", form.category);
    if (form.subcategory) params.set("subcategory", form.subcategory);
    return `${base}/add?${params.toString()}`;
  };

  // The preview is the passage as it stands in the form, saved or not.
  const preview = useMemo(() => ({
    _id: id || "new",
    title: form.title,
    kind: form.kind,
    instructions: form.instructions,
    text: form.kind === "text" ? form.text : "",
    imageUrl: form.kind === "text" ? form.imageUrl : "",
    file: form.kind === "pdf" && form.file?.key ? { name: form.file.name } : null,
  }), [form, id]);

  // Until it loads, an existing passage is "Passage"; a new one stays "New passage".
  const trail = passageTrail({ isLibrary, passage: isNew ? null : { _id: id, ...saved } });

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <PageHeader icon={BookOpen} title="Passage" breadcrumbs={trail} />
        <Shimmer height={90} count={4} />
      </div>
    );
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-4 has-form-actionbar">
      <PageHeader
        icon={BookOpen}
        title={isNew ? "New passage" : (saved?.title || "Passage")}
        description="A paragraph or a document, and the questions students answer about it"
        breadcrumbs={trail}
      />

      <form onSubmit={save} className="tw-grid tw-gap-4 min-[1200px]:tw-grid-cols-[minmax(0,1fr)_minmax(0,1fr)] min-[1200px]:tw-items-start">
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>Passage</AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <PassageForm value={form} onChange={update} categories={categories} loadFileUrl={loadFileUrl} />
          </AppCardContent>
        </AppCard>

        <AppCard className="min-[1200px]:tw-sticky min-[1200px]:tw-top-4">
          <AppCardHeader>
            <AppCardTitle>
              <span className="tw-inline-flex tw-items-center tw-gap-2">
                <Eye size={16} aria-hidden="true" /> As students see it
              </span>
            </AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            {form.kind === "pdf" && !form.file?.key ? (
              <p className="tw-m-0 tw-text-sm tw-text-content-muted">Upload the document to see it here.</p>
            ) : form.kind === "text" && !form.text.trim() ? (
              <p className="tw-m-0 tw-text-sm tw-text-content-muted">Start writing the passage to see it here.</p>
            ) : (
              <div className="tw-max-h-[70vh] tw-overflow-y-auto">
                <PassageView
                  passage={preview}
                  loadFileUrl={loadFileUrl}
                  cacheKey={`editor:${fileKey}`}
                  rangeLabel={questions.length ? `Questions 1–${questions.length}` : ""}
                  headingLevel={3}
                />
              </div>
            )}
          </AppCardContent>
        </AppCard>

        <FormActionBar>
          <AppButton type="button" variant="ghost" onClick={() => navigate(parentOf(trail))}>
            Cancel
          </AppButton>
          {!isNew && (
            <AppButton type="button" variant="outline-destructive" onClick={() => setConfirmDelete(true)}>
              <Trash2 size={16} /> Delete
            </AppButton>
          )}
          <AppButton type="submit" disabled={saving}>
            <Save size={16} /> {saving ? "Saving…" : isNew ? "Save and add questions" : "Save passage"}
          </AppButton>
        </FormActionBar>
      </form>

      {!isNew && (
        <AppCard>
          <AppCardHeader>
            <AppCardTitle>
              <span className="tw-inline-flex tw-items-center tw-gap-2">
                <ListOrdered size={16} aria-hidden="true" /> Questions about this passage
                <span className="tw-text-sm tw-font-normal tw-text-content-muted">{questions.length}</span>
              </span>
            </AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            {questions.length > 0 && (
              <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">
                  A paper always asks these together, in this order — even when it shuffles its questions.
                </p>
                <AppButton size="sm" onClick={() => navigate(addQuestionUrl())}>
                  <Plus size={16} /> Add question
                </AppButton>
              </div>
            )}
            {questions.length === 0 ? (
              <EmptyState
                icon={ListOrdered}
                title="No questions yet"
                description="Add the questions students answer about this passage. Any question type works — single choice, multi select, text, numeric."
                action={<AppButton onClick={() => navigate(addQuestionUrl())}><Plus size={16} /> Add the first question</AppButton>}
              />
            ) : (
              <>
                <ol className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
                  {questions.map((q, i) => (
                    <li
                      key={q._id}
                      className="tw-flex tw-items-start tw-gap-3 tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface tw-px-3 tw-py-2.5"
                    >
                      <span className="tw-mt-0.5 tw-inline-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-muted tw-text-xs tw-font-semibold tw-text-content">
                        {i + 1}
                      </span>
                      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-1">
                        <span className="tw-line-clamp-2 tw-text-sm tw-text-content">
                          <MathText text={q.questionText} />
                        </span>
                        <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-xs tw-text-content-muted">
                          <AppBadge variant="secondary" size="sm">{TYPE_LABELS[q.questionType] || q.questionType}</AppBadge>
                          {q.marks} mark{q.marks === 1 ? "" : "s"}
                          {q.difficulty ? ` · ${q.difficulty}` : ""}
                        </span>
                      </div>
                      <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-0.5">
                        <IconButton icon={ArrowUp} label={`Move question ${i + 1} up`} variant="ghost" size="sm" disabled={i === 0} onClick={() => move(i, -1)} />
                        <IconButton icon={ArrowDown} label={`Move question ${i + 1} down`} variant="ghost" size="sm" disabled={i === questions.length - 1} onClick={() => move(i, 1)} />
                        <IconButton icon={Pencil} label={`Edit question ${i + 1}`} variant="ghost" size="sm" onClick={() => navigate(`${base}/edit/${q._id}?from=passage`)} />
                      </div>
                    </li>
                  ))}
                </ol>
              </>
            )}
          </AppCardContent>
        </AppCard>
      )}

      <ConfirmModal
        show={confirmDelete}
        onHide={() => setConfirmDelete(false)}
        onConfirm={remove}
        loading={deleting}
        title="Delete this passage?"
        message={questions.length
          ? `The passage and its ${questions.length} question${questions.length === 1 ? "" : "s"} will be removed from the bank. Exams already taken keep their copy.`
          : "The passage will be removed from the bank."}
        confirmText="Delete"
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
}
